"use client";

import { useSearchParams } from "next/navigation";
import {
  ITM_CAMPAIGN_ID_COOKIE,
  deleteClientTrackingCookie,
  getOrCreateClientTrackingId,
  hasClientCampaignAttributionConsent,
  watchCampaignAttributionConsent,
} from "privacy-consent";
import { useEffect } from "react";
import { useCampaignMutation } from "../api/campaign-set/front";

export const CampaignTracker = ({ referer }: { referer: string | null }) => {
  const params = useSearchParams();

  const [setCampaign, { error }] = useCampaignMutation({
    onCompleted: ({ success }) => {
      if (success) {
        console.log("success");
      } else {
        console.error("error", error);
      }
    },
  });

  // http://localhost:3001/voyage/croisiere-evenement-en-egypte-8195/?at_operation_id=370&at_medium=medium-test&at_campaign=campaign-test&at_type=&at_identifier=id-test&at_format=NL1&at_creation=&at_variant=
  const atMedium = params.get("at_medium") || undefined;
  const atCampaign = params.get("at_campaign") || undefined;
  const atType = params.get("at_type") || undefined;
  const atIdentifier = params.get("at_identifier") || undefined;
  const atFormat = params.get("at_format") || undefined;
  const atCreation = params.get("at_creation") || undefined;
  const atVariant = params.get("at_variant") || undefined;
  const utmSource = params.get("utm_source") || undefined;
  const atOperationId = params.get("at_operation_id") || undefined;
  const gclid = params.get("gclid") || undefined;
  const fbclid = params.get("fbclid") || undefined;

  const data = {
    atMedium,
    atCampaign,
    atType,
    atIdentifier,
    atFormat,
    atCreation,
    atVariant,
    utmSource,
    atOperationId,
    referer: referer || "direct",
    fbclid,
    gclid,
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    let sent = false;
    let active = true;
    let retryCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const sendCampaign = async () => {
      if (!active || sent || !hasClientCampaignAttributionConsent()) return;

      sent = true;
      getOrCreateClientTrackingId(ITM_CAMPAIGN_ID_COOKIE);
      const result = await setCampaign(data);

      if (!active || !hasClientCampaignAttributionConsent()) {
        sent = false;
        return;
      }

      if (!result.data?.success && retryCount < 2) {
        sent = false;
        retryCount += 1;
        retryTimer = setTimeout(() => void sendCampaign(), 500 * 2 ** retryCount);
      }
    };

    const stopWatching = watchCampaignAttributionConsent((allowed) => {
      if (!allowed) {
        sent = false;
        retryCount = 0;
        if (retryTimer) clearTimeout(retryTimer);
        deleteClientTrackingCookie(ITM_CAMPAIGN_ID_COOKIE);
        return;
      }

      retryCount = 0;
      void sendCampaign();
    });

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      stopWatching();
    };
  }, []);

  return null;
};
